import { Suspense } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { faEdit, faEye, faEyeSlash, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faClone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as DeleteWorkflowAlertDialog from 'assets/js/pages/engage/workflows/components/DeleteWorkflowAlertDialog';
import { UnpublishedChangesBadge } from 'assets/js/pages/engage/workflows/components/UnpublishedChangesBadge';
import { UpdateStatusButton } from 'assets/js/pages/engage/workflows/components/UpdateStatusButton';
import { WorkflowDetailsPreview } from 'assets/js/pages/engage/workflows/components/WorkflowDetailsPreview';
import { WorkflowStatusBadge } from 'assets/js/pages/engage/workflows/components/WorkflowStatusBadge';
import { useDuplicateWorkflow } from 'assets/js/pages/engage/workflows/hooks/useDuplicateWorkflow';
import { useInitializePreviewStore } from 'assets/js/pages/engage/workflows/hooks/useInitializePreviewStore';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';

const WorkflowDetailsHeader = () => {
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const { mutate: duplicateWorkflow, isPending: isDuplicatePending } = useDuplicateWorkflow({
        onSuccess: ({ id }) => history.push(`/accounts/${accountId}/workflows/${id}`),
    });

    const resetNodeStore = useReactFlowStore(state => state.reset);

    useInitializePreviewStore();

    if (!workflow) return null;

    return (
        <Box>
            <Collapsible.Root defaultOpen className="group/preview">
                <div className="flex flex-col items-start justify-between gap-4 px-6 py-4 md:flex-row md:items-center">
                    <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                        <h1 className="flex items-start gap-2 text-xl font-medium md:items-center">
                            <span>{workflow.icon}</span>
                            <span>{workflow.name}</span>
                        </h1>
                        <WorkflowStatusBadge status={workflow.status} />
                        {workflow.has_draft_version ? <UnpublishedChangesBadge /> : null}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center">
                            <Tooltip.Root>
                                <Collapsible.Trigger asChild>
                                    <Tooltip.Trigger asChild>
                                        <Button
                                            variant="transparent"
                                            size="sm"
                                            shape={'square'}
                                            aria-label="Toggle Preview"
                                        >
                                            <FontAwesomeIcon
                                                icon={faEyeSlash}
                                                size="lg"
                                                className="text-gray-600 group-data-[state=closed]/preview:hidden"
                                            />
                                            <FontAwesomeIcon
                                                icon={faEye}
                                                size="lg"
                                                className="text-gray-600 group-data-[state=open]/preview:hidden"
                                            />
                                        </Button>
                                    </Tooltip.Trigger>
                                </Collapsible.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <Tooltip.Arrow inverse />
                                        <p>Toggle preview</p>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <Button
                                        variant="transparent"
                                        shape={'square'}
                                        size="sm"
                                        onClick={() => {
                                            resetNodeStore();
                                            history.push(
                                                `/accounts/${accountId}/workflows/${workflow.id}/build`,
                                            );
                                        }}
                                        aria-label="Edit"
                                    >
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                            size="lg"
                                            className="text-gray-600"
                                        />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <Tooltip.Arrow inverse />
                                        <p>Edit</p>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            <Tooltip.Root>
                                <Tooltip.Trigger asChild>
                                    <Button
                                        variant="transparent"
                                        shape={'square'}
                                        size="sm"
                                        disabled={isDuplicatePending}
                                        onClick={e => {
                                            e.preventDefault();
                                            duplicateWorkflow({ workflowId: workflow.id });
                                        }}
                                        aria-label="Duplicate"
                                    >
                                        <FontAwesomeIcon
                                            icon={faClone}
                                            size="lg"
                                            className="text-gray-600"
                                        />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <Tooltip.Arrow inverse />
                                        <p>Duplicate</p>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>

                            <Tooltip.Root>
                                <DeleteWorkflowAlertDialog.Root
                                    workflowId={workflow.id}
                                    shouldRedirect
                                    onSuccess={() =>
                                        history.replace(`/accounts/${accountId}/workflows`)
                                    }
                                >
                                    <Tooltip.Trigger asChild>
                                        <DeleteWorkflowAlertDialog.Trigger asChild>
                                            <Button
                                                variant="transparent"
                                                shape={'square'}
                                                size="sm"
                                                aria-label="Delete"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    size="lg"
                                                    className="text-gray-600"
                                                />
                                            </Button>
                                        </DeleteWorkflowAlertDialog.Trigger>
                                    </Tooltip.Trigger>
                                </DeleteWorkflowAlertDialog.Root>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <Tooltip.Arrow inverse />
                                        <p>Delete</p>
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </div>
                        <UpdateStatusButton />
                    </div>
                </div>
                <Collapsible.Content>
                    <div className="border-t border-gray-200 p-6">
                        <div className="relative flex h-[550px] flex-col overflow-hidden rounded-md bg-gray-100">
                            <Suspense fallback={<Loader />}>
                                <WorkflowDetailsPreview />
                            </Suspense>
                        </div>
                    </div>
                </Collapsible.Content>
            </Collapsible.Root>
        </Box>
    );
};

export { WorkflowDetailsHeader };
